import { Button, Group, Modal, Paper, Stack, Text } from '@mantine/core'
import { useSessionInfo } from '../contexts/SessionContext'
import { DeviceAdvertisementsForHost } from '../models/DeviceAdvertisementsForHost'
import { AdStatuses, COLORS } from '../utils/constants'

interface AdStatusModalProps {
  ad: DeviceAdvertisementsForHost | null
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  isUpdating: boolean
}

export function AdStatusModal({
  ad,
  isOpen,
  onClose,
  onConfirm,
  isUpdating,
}: AdStatusModalProps) {
  const { session } = useSessionInfo()
  if (!ad) return null
  const isOwnAd = session?.organizationId === ad.organizationId
  const isDisabled = ad.status === AdStatuses.Disabled

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        isDisabled && isOwnAd
          ? 'Re-Enable Advertisement?'
          : 'Disable Advertisement?'
      }
      centered
      size='lg'
      styles={{
        title: {
          fontSize: '1.5rem',
          fontWeight: 500,
        },
      }}
    >
      <Stack gap='xl' py='md'>
        <Stack gap='xs'>
          <Text size='lg'>
            {isDisabled && isOwnAd
              ? `Are you sure you want to re-enable this advertisement at ${ad.locationName} - ${ad.deviceName}?`
              : `Are you sure you want to disable this advertisement at ${ad.locationName} - ${ad.deviceName}?`}
          </Text>
          <Text size='sm' c='dimmed' style={{ fontStyle: 'italic' }}>
            {isDisabled && isOwnAd
              ? 'Note: The advertisement will start running within 5 minutes.'
              : 'Note: The advertisement will stop running within 5 minutes.'}
          </Text>
          {!isOwnAd && !isDisabled && (
            <Paper bg={COLORS.HiddenGemzRed + '10'} p='md' radius='md'>
              <Stack gap='xs'>
                <Text size='sm' c={COLORS.HiddenGemzRed} fw={700}>
                  ⚠️ Important Warning
                </Text>
                <Text size='sm' c={COLORS.HiddenGemzRed}>
                  By disabling this advertisement, you will immediately stop it
                  from running. The advertiser will not be refunded for any
                  remaining days. Please only use this option if it is
                  absolutely necessary.
                </Text>
              </Stack>
            </Paper>
          )}
        </Stack>
        <Group gap='md' justify='space-between'>
          <Button
            variant='outline'
            onClick={onClose}
            disabled={isUpdating}
            size='md'
            styles={{
              label: {
                fontSize: '0.9rem',
              },
            }}
          >
            {isDisabled && isOwnAd ? 'No, Keep Disabled' : 'No, Keep Running'}
          </Button>
          <Button
            color={COLORS.HiddenGemzRed}
            onClick={onConfirm}
            loading={isUpdating}
            loaderProps={{ type: 'dots' }}
            size='md'
            styles={{
              label: {
                fontSize: '0.9rem',
              },
            }}
          >
            {isDisabled && isOwnAd ? 'Yes, Re-Enable Ad' : 'Yes, Disable Ad'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
